import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Plus, Upload, Search, Edit, Trash2, Package, DollarSign } from 'lucide-react';
import Papa from 'papaparse';

interface Product {
  varenummer: string;
  navn: string;
  beskrivelse: string;
  enhet: string;
  pris_eks_mva: number;
  pris_inkl_mva: number;
  leverandor: string;
  underkategori: string;
}

const Produkter: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortBy, setSortBy] = useState<'navn' | 'varenummer' | 'leverandor'>('navn');
  const [isLoading, setIsLoading] = useState(false);
  const [importProgress, setImportProgress] = useState<{show: boolean; current: number; total: number}>({
    show: false,
    current: 0,
    total: 0
  });
  
  const [formData, setFormData] = useState<Partial<Product>>({
    varenummer: '',
    navn: '',
    beskrivelse: '',
    enhet: 'stk',
    pris_eks_mva: 0,
    pris_inkl_mva: 0,
    leverandor: '',
    underkategori: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  // Reset til side 1 ved søk eller sortering
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, sortBy]);

  const fetchProducts = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .order('navn');
    
    if (error) {
      console.error('Error fetching products:', error);
    } else {
      setProducts(data || []);
    }
    setIsLoading(false);
  };

  // Forbedret søk med normalisering
  const normalizeString = (str: string) => {
    return str
      .toLowerCase()
      .replace(/[øå]/g, 'o')
      .replace(/[æ]/g, 'a')
      .replace(/[éè]/g, 'e');
  };

  const filteredProducts = products.filter(product => {
    const normalizedSearch = normalizeString(searchTerm);
    return (
      normalizeString(product.navn).includes(normalizedSearch) ||
      product.varenummer.toLowerCase().includes(searchTerm.toLowerCase()) ||
      normalizeString(product.leverandor).includes(normalizedSearch) ||
      normalizeString(product.beskrivelse || '').includes(normalizedSearch)
    );
  });

  // Sortering
  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'navn':
        return a.navn.localeCompare(b.navn);
      case 'varenummer':
        return a.varenummer.localeCompare(b.varenummer);
      case 'leverandor':
        return a.leverandor.localeCompare(b.leverandor);
      default:
        return 0;
    }
  });

  // Paginering
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProducts = sortedProducts.slice(startIndex, endIndex);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Vis advarsel for store filer
    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 10) {
      const proceed = window.confirm(
        `Filen er ${fileSizeMB.toFixed(1)} MB stor og inneholder sannsynligvis mange produkter.\n\n` +
        `Import kan ta flere minutter. Vil du fortsette?\n\n` +
        `Tips: Hold nettleseren åpen til importen er ferdig.`
      );
      if (!proceed) return;
    }

    Papa.parse(file, {
      header: true,
      delimiter: ';',
      skipEmptyLines: true,
      encoding: 'UTF-8',
      complete: async (results) => {
        const parsedProducts: Product[] = [];
        const seenVarenummer = new Set<string>();
        const duplicates: any[] = [];
        
        results.data.forEach((row: any) => {
          if (!row.varenummer || !row.navn) return;
          
          const varenummer = String(row.varenummer).trim();
          
          if (seenVarenummer.has(varenummer)) {
            duplicates.push({
              varenummer: varenummer,
              navn: row.navn
            });
            return;
          }
          
          seenVarenummer.add(varenummer);
          
          const product: Product = {
            varenummer: varenummer,
            navn: row.navn || '',
            beskrivelse: row.beskrivelse || row.leverandør || '',
            enhet: row.enhet || 'stk',
            pris_eks_mva: parseFloat(row.priseksmva) || 0,
            pris_inkl_mva: parseFloat(row.prisinklmva) || 0,
            leverandor: row.leverandør || row.beskrivelse || '',
            underkategori: row.underkategori || ''
          };
          
          parsedProducts.push(product);
        });

        if (duplicates.length > 0) {
          console.warn(`Hoppet over ${duplicates.length} duplikater i filen`);
        }

        if (parsedProducts.length > 0) {
          const confirmImport = window.confirm(
            `Fant ${parsedProducts.length} produkter i filen.\n\n` +
            `Eksisterende produkter vil bli oppdatert basert på varenummer.\n` +
            `Nye produkter vil bli lagt til.\n\n` +
            `Vil du fortsette?`
          );
          
          if (confirmImport) {
            setImportProgress({ show: true, current: 0, total: parsedProducts.length });
            await saveProductsBatch(parsedProducts);
          }
        } else {
          alert('Ingen gyldige produkter funnet i filen.');
        }
      },
      error: (error) => {
        console.error('Error parsing CSV:', error);
        alert('Feil ved lesing av CSV-fil. Sjekk at filen er korrekt formatert.');
      }
    });
  };

  const saveProductsBatch = async (productsToSave: Product[]) => {
    try {
      const batchSize = 100;
      let savedCount = 0;
      let errorCount = 0;
      
      for (let i = 0; i < productsToSave.length; i += batchSize) {
        const batch = productsToSave.slice(i, i + batchSize);
        
        const { data, error } = await supabase
          .from('products')
          .upsert(batch, { 
            onConflict: 'varenummer'
          });
        
        if (error) {
          console.error(`Error saving batch ${i/batchSize + 1}:`, error);
          errorCount += batch.length;
        } else {
          savedCount += batch.length;
        }
        
        // Oppdater progress
        setImportProgress(prev => ({ ...prev, current: i + batch.length }));
      }
      
      setImportProgress({ show: false, current: 0, total: 0 });
      alert(`Import fullført!\nImportert: ${savedCount} produkter\nFeilet: ${errorCount} produkter`);
      fetchProducts();
    } catch (error) {
      console.error('Error saving products:', error);
      alert('Feil ved lagring av produkter. Se konsollen for detaljer.');
      setImportProgress({ show: false, current: 0, total: 0 });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingProduct) {
      const { error } = await supabase
        .from('products')
        .update(formData)
        .eq('varenummer', editingProduct.varenummer);
      
      if (error) {
        console.error('Error updating product:', error);
        alert('Feil ved oppdatering av produkt');
      } else {
        setEditingProduct(null);
        setShowAddForm(false);
        fetchProducts();
      }
    } else {
      const { error } = await supabase
        .from('products')
        .insert([formData]);
      
      if (error) {
        console.error('Error adding product:', error);
        alert('Feil ved lagring av produkt');
      } else {
        setShowAddForm(false);
        setFormData({
          varenummer: '',
          navn: '',
          beskrivelse: '',
          enhet: 'stk',
          pris_eks_mva: 0,
          pris_inkl_mva: 0,
          leverandor: '',
          underkategori: ''
        });
        fetchProducts();
      }
    }
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setFormData(product);
    setShowAddForm(true);
  };

  const handleDelete = async (varenummer: string) => {
    if (window.confirm('Er du sikker på at du vil slette dette produktet?')) {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('varenummer', varenummer);
      
      if (error) {
        console.error('Error deleting product:', error);
        alert('Feil ved sletting av produkt');
      } else {
        fetchProducts();
      }
    }
  };

  // Beregn MVA prosent
  const calculateVatPercent = (eksklMva: number, inklMva: number): string => {
    if (eksklMva === 0) return '0%';
    const vatPercent = ((inklMva - eksklMva) / eksklMva) * 100;
    return `${vatPercent.toFixed(0)}%`;
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">
        Produktadministrasjon ({products.length} produkter)
      </h1>
      
      {/* Handlingsknapper */}
      <div className="mb-4 flex gap-2">
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded flex items-center gap-2"
        >
          <Plus size={20} />
          Legg til produkt
        </button>
        
        <label className="bg-green-500 text-white px-4 py-2 rounded flex items-center gap-2 cursor-pointer">
          <Upload size={20} />
          Importer fra CSV
          <input
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            className="hidden"
          />
        </label>
      </div>

      {/* Import progress */}
      {importProgress.show && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold mb-2">Importerer produkter...</h3>
          <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
            <div 
              className="bg-blue-600 h-4 rounded-full transition-all duration-300"
              style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600">
            {importProgress.current} av {importProgress.total} produkter importert
          </p>
        </div>
      )}

      {/* Søk og sortering */}
      <div className="mb-4 flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Søk etter produkt (navn, varenummer, leverandør)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value as 'navn' | 'varenummer' | 'leverandor')}
          className="px-3 py-2 border rounded"
        >
          <option value="navn">Sorter etter navn</option>
          <option value="varenummer">Sorter etter varenummer</option>
          <option value="leverandor">Sorter etter leverandør</option>
        </select>
      </div>

      {/* Statistikk */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div className="bg-blue-50 p-3 rounded-lg">
          <div className="text-sm text-blue-600">Totalt produkter</div>
          <div className="text-2xl font-bold text-blue-900">{products.length}</div>
        </div>
        <div className="bg-green-50 p-3 rounded-lg">
          <div className="text-sm text-green-600">Filtrert</div>
          <div className="text-2xl font-bold text-green-900">{filteredProducts.length}</div>
        </div>
        <div className="bg-orange-50 p-3 rounded-lg">
          <div className="text-sm text-orange-600">Leverandører</div>
          <div className="text-2xl font-bold text-orange-900">
            {new Set(products.map(p => p.leverandor)).size}
          </div>
        </div>
        <div className="bg-purple-50 p-3 rounded-lg">
          <div className="text-sm text-purple-600">Kategorier</div>
          <div className="text-2xl font-bold text-purple-900">
            {new Set(products.map(p => p.underkategori)).size}
          </div>
        </div>
      </div>

      {/* Paginering kontroller */}
      {sortedProducts.length > itemsPerPage && (
        <div className="flex justify-between items-center mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">
              Viser {startIndex + 1} - {Math.min(endIndex, sortedProducts.length)} av {sortedProducts.length} produkter
            </span>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-1 border border-gray-300 rounded text-sm"
            >
              <option value={25}>25 per side</option>
              <option value={50}>50 per side</option>
              <option value={100}>100 per side</option>
              <option value={200}>200 per side</option>
              <option value={500}>500 per side</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-300 disabled:hover:bg-gray-200"
            >
              ««
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-300 disabled:hover:bg-gray-200"
            >
              «
            </button>
            <span className="px-3 py-1 text-sm">
              Side {currentPage} av {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-300 disabled:hover:bg-gray-200"
            >
              »
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-300 disabled:hover:bg-gray-200"
            >
              »»
            </button>
          </div>
        </div>
      )}

      {/* Produkttabell */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-2 text-gray-600">Laster produkter...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="border p-2 text-left">Varenummer</th>
                <th className="border p-2 text-left">Navn</th>
                <th className="border p-2 text-left">Enhet</th>
                <th className="border p-2 text-right">Pris eks.</th>
                <th className="border p-2 text-right">Pris inkl.</th>
                <th className="border p-2 text-center">MVA</th>
                <th className="border p-2 text-left">Leverandør</th>
                <th className="border p-2 text-center">Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {currentProducts.map((product) => (
                <tr key={product.varenummer} className="hover:bg-gray-50">
                  <td className="border p-2 font-mono text-sm">{product.varenummer}</td>
                  <td className="border p-2">
                    <div className="font-medium">{product.navn}</div>
                    {product.beskrivelse && (
                      <div className="text-xs text-gray-500">{product.beskrivelse}</div>
                    )}
                  </td>
                  <td className="border p-2">{product.enhet}</td>
                  <td className="border p-2 text-right font-mono">
                    {product.pris_eks_mva.toFixed(2)}
                  </td>
                  <td className="border p-2 text-right font-mono font-semibold">
                    {product.pris_inkl_mva.toFixed(2)}
                  </td>
                  <td className="border p-2 text-center text-sm text-gray-600">
                    {calculateVatPercent(product.pris_eks_mva, product.pris_inkl_mva)}
                  </td>
                  <td className="border p-2">
                    <div className="text-sm">{product.leverandor}</div>
                    {product.underkategori && (
                      <div className="text-xs text-gray-500">{product.underkategori}</div>
                    )}
                  </td>
                  <td className="border p-2">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => handleEdit(product)}
                        className="text-blue-500 hover:text-blue-700"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(product.varenummer)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {currentProducts.length === 0 && !isLoading && (
        <div className="text-center text-gray-500 mt-8">
          <Package className="mx-auto text-gray-400 mb-3" size={48} />
          <p className="text-lg">Ingen produkter funnet</p>
          {searchTerm && (
            <p className="text-sm mt-1">Prøv et annet søkeord eller sjekk stavemåten</p>
          )}
        </div>
      )}

      {/* Legg til/Rediger produkt modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingProduct ? 'Rediger produkt' : 'Legg til nytt produkt'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block mb-1">Varenummer *</label>
                <input
                  type="text"
                  value={formData.varenummer}
                  onChange={(e) => setFormData({...formData, varenummer: e.target.value})}
                  className="w-full p-2 border rounded"
                  required
                  disabled={!!editingProduct}
                />
              </div>
              
              <div className="mb-4">
                <label className="block mb-1">Navn *</label>
                <input
                  type="text"
                  value={formData.navn}
                  onChange={(e) => setFormData({...formData, navn: e.target.value})}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block mb-1">Beskrivelse</label>
                <textarea
                  value={formData.beskrivelse}
                  onChange={(e) => setFormData({...formData, beskrivelse: e.target.value})}
                  className="w-full p-2 border rounded"
                  rows={3}
                />
              </div>
              
              <div className="mb-4">
                <label className="block mb-1">Enhet</label>
                <select
                  value={formData.enhet}
                  onChange={(e) => setFormData({...formData, enhet: e.target.value})}
                  className="w-full p-2 border rounded"
                >
                  <option value="stk">stk</option>
                  <option value="pak">pak</option>
                  <option value="kg">kg</option>
                  <option value="l">l</option>
                  <option value="m">m</option>
                  <option value="m2">m²</option>
                  <option value="m3">m³</option>
                </select>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block mb-1">Pris eks. MVA</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.pris_eks_mva}
                    onChange={(e) => setFormData({...formData, pris_eks_mva: parseFloat(e.target.value)})}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
                
                <div>
                  <label className="block mb-1">Pris inkl. MVA</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.pris_inkl_mva}
                    onChange={(e) => setFormData({...formData, pris_inkl_mva: parseFloat(e.target.value)})}
                    className="w-full p-2 border rounded"
                    required
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <label className="block mb-1">Leverandør</label>
                <input
                  type="text"
                  value={formData.leverandor}
                  onChange={(e) => setFormData({...formData, leverandor: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
              
              <div className="mb-4">
                <label className="block mb-1">Underkategori</label>
                <input
                  type="text"
                  value={formData.underkategori}
                  onChange={(e) => setFormData({...formData, underkategori: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
              
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  {editingProduct ? 'Oppdater' : 'Lagre'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingProduct(null);
                    setFormData({
                      varenummer: '',
                      navn: '',
                      beskrivelse: '',
                      enhet: 'stk',
                      pris_eks_mva: 0,
                      pris_inkl_mva: 0,
                      leverandor: '',
                      underkategori: ''
                    });
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                  Avbryt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Produkter;